import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  rectIntersection, closestCenter,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CircularProgress from '@mui/material/CircularProgress';
import CardCallsTab from '@/components/admin/dialpad/CardCallsTab';

import KanbanColumn from '@/components/workflow/KanbanColumn';
import CommentThread from '@/components/workflow/CommentThread';
import StatusHistoryLog from '@/components/workflow/StatusHistoryLog';
import {
  getBoard, getCards, getCard, createCard, moveCard, assignCard,
  addTag, removeTag, addComment, getChemists, getManagers, getJobTypes,
  archiveCard, unarchiveCard, getArchivedCards,
  getTrelloBoardsLists, getTrelloBoardMembers, sendCardToTrello, suggestTrelloTitle,
} from '@/services/workflowService';

const BOARD_ID    = 3;
const COMPLETE_KEY = 'complete';

// Column accent colours (used for header + left border)
const COL_COLORS = {
  new:        { accent: '#FFAF3C', bg: '#FFFBF0', header: '#FEF3C7', text: '#92400E' },
  working_on: { accent: '#3B82F6', bg: '#F0F8FF', header: '#DBEAFE', text: '#1E40AF' },
  complete:   { accent: '#16A34A', bg: '#F0FDF4', header: '#DCFCE7', text: '#166534' },
};

// ── Searchable manager combobox ───────────────────────────────────────────────
const ManagerCombobox = ({ managers, value, onChange, placeholder = 'Search manager…' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const [pos, setPos]     = useState(null); // { top, left, width, maxHeight, flipped }
  const wrapRef           = useRef(null);
  const inputRef          = useRef(null);
  const listRef           = useRef(null);

  const selected = managers.find(m => String(m.Id) === String(value));
  const filtered = useMemo(() => {
    if (!query.trim()) return managers;
    const q = query.toLowerCase();
    return managers.filter(m => (m.Name || '').toLowerCase().includes(q));
  }, [query, managers]);

  // Recompute the dropdown's viewport position so it isn't clipped by any
  // scrollable ancestor (e.g. the job card modal body) and flips upward
  // when there isn't enough room below the input.
  const updatePosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const desired = 240; // preferred dropdown height
    const flipped = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(desired, flipped ? spaceAbove : spaceBelow));
    setPos({
      left: rect.left,
      width: rect.width,
      top: flipped ? undefined : rect.bottom + 4,
      bottom: flipped ? window.innerHeight - rect.top + 4 : undefined,
      maxHeight,
      flipped,
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) && !listRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const handleSelect = (manager) => { onChange(String(manager.Id)); setQuery(''); setOpen(false); };
  const handleClear  = () => { onChange(''); setQuery(''); setOpen(false); inputRef.current?.focus(); };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-1 mt-0.5 border border-gray-200 rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-300 transition">
        <input
          ref={inputRef}
          type="text"
          value={open ? query : (selected?.Name || '')}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          placeholder={placeholder}
          className="flex-1 px-2 py-1.5 text-sm focus:outline-none bg-transparent"
        />
        {value && (
          <button type="button" onClick={handleClear} className="px-2 text-gray-400 hover:text-gray-600 transition">
            <CloseIcon style={{ fontSize: 14 }} />
          </button>
        )}
      </div>
      {open && pos && createPortal(
        <div
          ref={listRef}
          className="fixed z-[1000] bg-white border border-gray-200 rounded-xl shadow-xl overflow-y-auto"
          style={{
            left: pos.left,
            width: pos.width,
            top: pos.top,
            bottom: pos.bottom,
            maxHeight: pos.maxHeight,
          }}
        >
          <button type="button" onMouseDown={(e) => { e.preventDefault(); handleClear(); }}
            className={`w-full text-left px-4 py-2 text-sm text-gray-400 italic hover:bg-blue-50 transition ${!value ? 'bg-blue-50 font-semibold text-blue-700 not-italic' : ''}`}>
            Unassigned
          </button>
          {filtered.length === 0 ? (
            <p className="px-4 py-2 text-xs text-gray-400 italic">No matches for "{query}"</p>
          ) : filtered.map(m => (
            <button key={m.Id} type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(m); }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-800 transition ${String(m.Id) === String(value) ? 'bg-blue-50 text-blue-800 font-semibold' : 'text-gray-700'}`}>
              {m.Name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Job card tile ─────────────────────────────────────────────────────────────
const JobCardTile = ({ card, colKey, onClick, onArchive }) => {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(card.id) });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const colors = COL_COLORS[colKey] || COL_COLORS.new;

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, borderLeft: `3px solid ${colors.accent}` }}
      {...attributes} {...listeners}
      onClick={() => onClick(card)}
      className="w-full bg-white rounded-xl border border-gray-100 p-4 cursor-pointer hover:shadow-md hover:border-gray-200 transition-all duration-150 group"
    >
      {/* Pharmacy + quick links */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <p className="text-sm font-bold leading-tight truncate" style={{ color: colors.text }}>
            {card.chemist_name || `Chemist #${card.chemist_id}`}
          </p>
          {card.chemist_uuid && (
            <button title="Open chemist page" onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); navigate(`/admin-functions/chemist/${encodeURIComponent(card.chemist_uuid)}`); }}
              className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-blue-600 hover:bg-blue-50 transition opacity-0 group-hover:opacity-100">
              <OpenInNewIcon style={{ fontSize: 13 }} />
            </button>
          )}
          {card.chemist_email && (
            <a href={`mailto:${card.chemist_email}`} title={`Email ${card.chemist_email}`}
              onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
              className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-blue-600 hover:bg-blue-50 transition opacity-0 group-hover:opacity-100">
              <EmailOutlinedIcon style={{ fontSize: 13 }} />
            </a>
          )}
          {(card.chemist_phone || card.pharmacy_phone) && (
            <a href={`tel:${card.chemist_phone || card.pharmacy_phone}`}
              title={`Call ${card.chemist_phone || card.pharmacy_phone}`}
              onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
              className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-green-600 hover:bg-green-50 transition opacity-0 group-hover:opacity-100">
              <PhoneOutlinedIcon style={{ fontSize: 13 }} />
            </a>
          )}
        </div>
        {/* Archive button — only on complete column */}
        {onArchive && (
          <button title="Archive this job" onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onArchive(card.id); }}
            className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-amber-600 hover:bg-amber-50 transition opacity-0 group-hover:opacity-100">
            <ArchiveOutlinedIcon style={{ fontSize: 15 }} />
          </button>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-gray-700 leading-relaxed line-clamp-3 mb-3">{card.description}</p>

      {/* Footer row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {card.job_type_name && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
              style={{ backgroundColor: card.job_type_color || '#6b7280' }}>
              {card.job_type_name}
            </span>
          )}
          {card.source === 'Cherub' && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
              Cherub
            </span>
          )}
          {card.trello_card_id && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              Trello ↗
            </span>
          )}
        </div>
        {card.assigned_to_name && (
          <span className="flex items-center gap-0.5 text-[11px] text-gray-400 flex-shrink-0">
            <PersonOutlinedIcon style={{ fontSize: 12 }} /> {card.assigned_to_name}
          </span>
        )}
      </div>
    </div>
  );
};

// ── New job modal ─────────────────────────────────────────────────────────────
const NewJobModal = ({ onClose, onCreated, firstColumnId }) => {
  const [chemists, setChemists] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [form, setForm] = useState({ chemist_id: '', description: '', job_type_id: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getChemists(), getJobTypes()])
      .then(([c, jt]) => { setChemists(c || []); setJobTypes(jt || []); })
      .catch(console.error);
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createCard({ board_id: BOARD_ID, column_id: firstColumnId, chemist_id: Number(form.chemist_id), description: form.description, job_type_id: form.job_type_id ? Number(form.job_type_id) : null, source: 'User' });
      onCreated();
      onClose();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#002169]">New Job Card</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><CloseIcon /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pharmacy *</label>
            <select required value={form.chemist_id} onChange={(e) => set('chemist_id', e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300">
              <option value="">Select pharmacy…</option>
              {chemists.map(c => <option key={c.chemistID} value={c.chemistID}>{c.chemistName}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Job Type</label>
            <select value={form.job_type_id} onChange={(e) => set('job_type_id', e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300">
              <option value="">Select type…</option>
              {jobTypes.map(jt => <option key={jt.id} value={jt.id}>{jt.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description *</label>
            <textarea required rows={4} value={form.description} onChange={(e) => set('description', e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 resize-none"
              placeholder="Describe the job…" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-[#FFAF3C] text-[#002169] rounded-lg text-sm font-bold hover:bg-amber-400 disabled:opacity-50 transition">
              {saving ? 'Creating…' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Job card detail modal ─────────────────────────────────────────────────────
const JobCardDetailModal = ({ cardId, boardColumns, completeColId, onClose, onUpdate, onArchive }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const [trelloPicker, setTrelloPicker]                 = useState(false);
  const [trelloBoards, setTrelloBoards]                 = useState(null);
  const [trelloBoardsLoading, setTrelloBoardsLoading]   = useState(false);
  const [selectedBoardId, setSelectedBoardId]           = useState('');
  const [selectedListId, setSelectedListId]             = useState('');
  const [trelloNotes, setTrelloNotes]                   = useState('');
  const [trelloMembers, setTrelloMembers]               = useState(null);
  const [trelloMembersLoading, setTrelloMembersLoading] = useState(false);
  const [selectedMemberId, setSelectedMemberId]         = useState('');
  const [trelloSending, setTrelloSending]               = useState(false);
  const [trelloSent, setTrelloSent]                     = useState(null);
  const [trelloError, setTrelloError]                   = useState(null);
  const [trelloCompletePrompt, setTrelloCompletePrompt] = useState(false);
  const [trelloCompleting, setTrelloCompleting]         = useState(false);
  const [trelloTitle, setTrelloTitle]                   = useState('');
  const [trelloTitleLoading, setTrelloTitleLoading]     = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [card, mgrs] = await Promise.all([getCard(cardId), getManagers()]);
      setData(card);
      setManagers((mgrs || []).filter(m => m.IsPHAdmin === 1 || m.IsPHAdmin === true));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [cardId]);

  useEffect(() => { reload(); }, [reload]);

  const handleColumnChange = async (columnId) => {
    setSaving(true);
    try {
      await moveCard(cardId, Number(columnId), {});
      await reload();
      onUpdate && onUpdate();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleAssign = async (userId) => {
    if (!userId) return;
    setSaving(true);
    try { await assignCard(cardId, Number(userId)); await reload(); onUpdate && onUpdate(); } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleAddComment = async (comment) => { await addComment(cardId, comment); await reload(); };

  const handleOpenTrello = async () => {
    setTrelloPicker(true);
    setTrelloSent(null);
    setTrelloError(null);
    setTrelloNotes('');
    setTrelloTitle('');

    // Fetch AI-suggested title and boards in parallel
    setTrelloTitleLoading(true);
    const titlePromise = suggestTrelloTitle(cardId)
      .then(res => setTrelloTitle(res?.title || ''))
      .catch(() => {})
      .finally(() => setTrelloTitleLoading(false));

    if (!trelloBoards) {
      setTrelloBoardsLoading(true);
      try {
        const res = await getTrelloBoardsLists();
        setTrelloBoards(res?.configured ? res.boards : []);
      } catch { setTrelloBoards([]); }
      setTrelloBoardsLoading(false);
    }

    await titlePromise;
  };

  const handleBoardSelect = async (boardId) => {
    setSelectedBoardId(boardId);
    setSelectedListId('');
    setTrelloMembers(null);
    setSelectedMemberId('');
    if (!boardId) return;
    setTrelloMembersLoading(true);
    try {
      const res = await getTrelloBoardMembers(boardId);
      setTrelloMembers(res?.members || []);
      if (res?.my_trello_member_id) setSelectedMemberId(res.my_trello_member_id);
    } catch { setTrelloMembers([]); }
    setTrelloMembersLoading(false);
  };

  const selectedBoardLists = useMemo(() => {
    if (!trelloBoards || !selectedBoardId) return [];
    return trelloBoards.find(b => b.id === selectedBoardId)?.lists || [];
  }, [trelloBoards, selectedBoardId]);

  const handleSendToTrello = async () => {
    if (!selectedListId) return;
    setTrelloSending(true);
    setTrelloError(null);
    try {
      const res = await sendCardToTrello(cardId, selectedListId, trelloNotes, selectedMemberId || undefined, trelloTitle.trim() || undefined);
      setTrelloSent({ url: res?.trello_card_url || null });
      setTrelloPicker(false);
      if (completeColId && data?.column_id !== completeColId) setTrelloCompletePrompt(true);
    } catch { setTrelloError('Failed to send to Trello. Check server config.'); }
    setTrelloSending(false);
  };

  const handleCompleteAfterTrello = async () => {
    if (!completeColId) return;
    setTrelloCompleting(true);
    try { await moveCard(cardId, completeColId, {}); onUpdate && onUpdate(); onClose(); } catch (e) { console.error(e); }
    setTrelloCompleting(false);
    setTrelloCompletePrompt(false);
  };

  const TABS = ['details', 'calls', 'comments', 'history'];
  const currentColKey = data ? boardColumns.find(c => c.id === data.column_id)?.key : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#FFAF3C] rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h2 className="text-lg font-bold text-[#002169] truncate">{data?.chemist_name || `Job #${cardId}`}</h2>
            {data?.chemist_uuid && (
              <button title="Open chemist page"
                onClick={() => navigate(`/admin-functions/chemist/${encodeURIComponent(data.chemist_uuid)}`)}
                className="flex-shrink-0 p-1 rounded-md text-[#002169]/60 hover:text-[#002169] hover:bg-[#002169]/10 transition">
                <OpenInNewIcon style={{ fontSize: 16 }} />
              </button>
            )}
            {data?.chemist_email && (
              <a href={`mailto:${data.chemist_email}`} title={`Email ${data.chemist_email}`}
                className="flex-shrink-0 p-1 rounded-md text-[#002169]/60 hover:text-[#002169] hover:bg-[#002169]/10 transition">
                <EmailOutlinedIcon style={{ fontSize: 16 }} />
              </a>
            )}
            {data?.chemist_phone && (
              <a href={`tel:${data.chemist_phone}`} title={`Call ${data.chemist_phone}`}
                className="flex-shrink-0 p-1 rounded-md text-[#002169]/60 hover:text-[#002169] hover:bg-[#002169]/10 transition">
                <PhoneOutlinedIcon style={{ fontSize: 16 }} />
              </a>
            )}
          </div>
          <button onClick={onClose} className="flex-shrink-0 text-[#002169]/60 hover:text-[#002169] transition ml-2"><CloseIcon /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 flex-shrink-0 bg-gray-50">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide transition-all
                ${activeTab === tab ? 'border-b-2 border-amber-500 text-amber-600 bg-white' : 'text-gray-500 hover:text-gray-700'}`}>
              {tab}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8"><CircularProgress size={24} style={{ color: '#FFAF3C' }} /></div>
          ) : !data ? <p className="text-gray-400 text-sm">Not found.</p> : (
            <>
              {activeTab === 'details' && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</label>
                    <p className="mt-1 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{data.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created By</label>
                      <p className="mt-0.5 text-xs text-gray-700">{data.created_by_name || '—'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</label>
                      <p className="mt-0.5 text-xs text-gray-700">{new Date(data.created_at).toLocaleDateString('en-AU')}</p>
                    </div>

                    {/* Move to column */}
                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Column</label>
                      <select
                        value={data.column_id || ''}
                        onChange={(e) => handleColumnChange(e.target.value)}
                        disabled={saving}
                        className="mt-0.5 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                      >
                        {boardColumns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </div>

                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Assigned To</label>
                      <ManagerCombobox managers={managers} value={data.assigned_to || ''} onChange={(id) => handleAssign(id)} placeholder="Search manager…" />
                    </div>
                  </div>

                  {(data.job_type_name || data.source === 'Cherub') && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {data.job_type_name && (
                        <span className="inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full text-white"
                          style={{ backgroundColor: data.job_type_color || '#6b7280' }}>
                          {data.job_type_name}
                        </span>
                      )}
                      {data.source === 'Cherub' && (
                        <span className="inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full bg-sky-100 text-sky-700">Cherub</span>
                      )}
                    </div>
                  )}
                </>
              )}
              {activeTab === 'calls'    && <CardCallsTab context="job" cardId={cardId} />}
              {activeTab === 'comments' && <CommentThread comments={data.comments || []} onAdd={handleAddComment} />}
              {activeTab === 'history'  && <StatusHistoryLog history={data.history || []} />}
            </>
          )}
        </div>

        {/* Trello picker */}
        {trelloPicker && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0 space-y-2.5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <SyncAltIcon style={{ fontSize: 13 }} /> Send to Trello
            </p>
            {trelloBoardsLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <CircularProgress size={13} style={{ color: '#0052cc' }} /> Loading boards…
              </div>
            ) : trelloBoards?.length === 0 ? (
              <p className="text-xs text-red-500">No Trello boards found. Check server configuration.</p>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2 flex-wrap items-center">
                  <select value={selectedBoardId} onChange={(e) => handleBoardSelect(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052cc] bg-white min-w-[140px]">
                    <option value="">Select board…</option>
                    {(trelloBoards || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                  {selectedBoardId && (
                    <select value={selectedListId} onChange={(e) => setSelectedListId(e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052cc] bg-white min-w-[140px]">
                      <option value="">Select column…</option>
                      {selectedBoardLists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  )}
                </div>
                {selectedBoardId && (
                  <div className="flex items-center gap-2">
                    {trelloMembersLoading ? (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <CircularProgress size={11} style={{ color: '#0052cc' }} /> Loading members…
                      </span>
                    ) : trelloMembers?.length > 0 ? (
                      <>
                        <label className="text-xs text-gray-500 whitespace-nowrap">Assign to:</label>
                        <select value={selectedMemberId} onChange={(e) => setSelectedMemberId(e.target.value)}
                          className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052cc] bg-white">
                          <option value="">Unassigned</option>
                          {trelloMembers.map(m => <option key={m.id} value={m.id}>{m.name} (@{m.username})</option>)}
                        </select>
                        {selectedMemberId && <span className="text-xs text-green-600 whitespace-nowrap">✓ saved</span>}
                      </>
                    ) : null}
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Card title</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={trelloTitle}
                      onChange={(e) => setTrelloTitle(e.target.value)}
                      placeholder={trelloTitleLoading ? 'Generating title…' : 'Card title…'}
                      disabled={trelloTitleLoading}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052cc] bg-white disabled:text-gray-400"
                    />
                    {trelloTitleLoading && (
                      <CircularProgress size={12} style={{ color: '#0052cc', position: 'absolute', right: 8, top: '50%', marginTop: -6 }} />
                    )}
                  </div>
                </div>
                <textarea value={trelloNotes} onChange={(e) => setTrelloNotes(e.target.value)}
                  placeholder="Add a note to the Trello card (optional)…" rows={2}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052cc] resize-none bg-white" />
                <div className="flex gap-2">
                  <button onClick={handleSendToTrello} disabled={!selectedListId || trelloSending}
                    className="px-3 py-1.5 bg-[#0052cc] text-white text-sm font-semibold rounded-lg hover:bg-[#003d99] disabled:opacity-40 transition">
                    {trelloSending ? 'Sending…' : 'Send to Trello'}
                  </button>
                  <button onClick={() => setTrelloPicker(false)} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 transition">Cancel</button>
                </div>
              </div>
            )}
            {trelloError && <p className="text-xs text-red-500">{trelloError}</p>}
          </div>
        )}

        {/* Post-Trello complete prompt */}
        {trelloCompletePrompt && (
          <div className="px-6 py-3 border-t border-amber-200 bg-amber-50 flex-shrink-0">
            <p className="text-sm font-semibold text-amber-800 mb-2">
              <CheckCircleOutlineIcon style={{ fontSize: 15, verticalAlign: 'middle', marginRight: 4 }} />
              Sent to Trello! Move this job to Completed?
            </p>
            <div className="flex gap-2">
              <button onClick={handleCompleteAfterTrello} disabled={trelloCompleting}
                className="flex-1 px-3 py-1.5 bg-[#002169] text-white rounded-lg text-sm font-semibold hover:bg-[#001244] disabled:opacity-50 transition">
                {trelloCompleting ? 'Moving…' : 'Yes, mark as Completed'}
              </button>
              <button onClick={() => setTrelloCompletePrompt(false)}
                className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition">
                No, keep it
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 flex gap-2 flex-wrap">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Close</button>
          {data && !trelloPicker && (() => {
            const syncedUrl = trelloSent?.url || data.trello_card_url || null;
            if (syncedUrl) {
              return (
                <div className="flex items-center gap-1">
                  <a href={syncedUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-4 py-2 border border-green-300 text-green-700 bg-green-50 rounded-lg text-sm font-semibold hover:bg-green-100 transition">
                    <CheckCircleOutlineIcon style={{ fontSize: 16 }} /> View in Trello
                  </a>
                  <button onClick={handleOpenTrello} className="px-2 py-2 text-gray-400 hover:text-[#0052cc] transition text-xs">Re-sync</button>
                </div>
              );
            }
            return (
              <button onClick={handleOpenTrello}
                className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:border-[#0052cc] hover:text-[#0052cc] transition">
                <SyncAltIcon style={{ fontSize: 16 }} />
                {data.trello_card_id ? 'Re-sync to Trello' : 'Send to Trello'}
              </button>
            );
          })()}
          {data && completeColId && data.column_id === completeColId && onArchive && (
            <button onClick={() => { onArchive(cardId); onClose(); }}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-100 hover:border-gray-400 transition">
              <ArchiveOutlinedIcon style={{ fontSize: 16 }} /> Archive
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Archived panel ────────────────────────────────────────────────────────────
const ArchivedPanel = ({ onClose, onUnarchived }) => {
  const [cards, setCards]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [unarchiving, setUnarchiving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const result = await getArchivedCards(BOARD_ID); setCards(result?.cards || []); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return cards;
    const q = search.toLowerCase();
    return cards.filter(c =>
      (c.chemist_name || '').toLowerCase().includes(q) ||
      (c.description  || '').toLowerCase().includes(q)
    );
  }, [cards, search]);

  const handleUnarchive = async (id) => {
    setUnarchiving(id);
    try { await unarchiveCard(id); setCards(prev => prev.filter(c => c.id !== id)); onUnarchived && onUnarchived(); } catch (e) { console.error(e); }
    setUnarchiving(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 bg-gray-700 rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-2">
            <ArchiveOutlinedIcon style={{ fontSize: 20, color: '#fff' }} />
            <h2 className="text-base font-bold text-white">Archived Jobs</h2>
            {!loading && <span className="text-xs bg-white/20 text-white rounded-full px-2 py-0.5">{cards.length}</span>}
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition"><CloseIcon /></button>
        </div>
        <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 16 }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search archived jobs…"
              className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white" />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8"><CircularProgress size={24} style={{ color: '#FFAF3C' }} /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8 italic">
              {search ? `No archived jobs match "${search}"` : 'No archived jobs yet.'}
            </p>
          ) : filtered.map(card => (
            <div key={card.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 hover:bg-amber-50 transition group">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-700 truncate">{card.chemist_name || `Chemist #${card.chemist_id}`}</p>
                <p className="text-sm text-gray-700 leading-tight line-clamp-2 mt-0.5">{card.description}</p>
                <span className="text-[10px] text-gray-400">
                  {new Date(card.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
              <button onClick={() => handleUnarchive(card.id)} disabled={unarchiving === card.id}
                className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-amber-300 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-40 transition">
                <UnarchiveOutlinedIcon style={{ fontSize: 13 }} />
                {unarchiving === card.id ? '…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Main board ────────────────────────────────────────────────────────────────
const JobBoard = () => {
  const [showNew, setShowNew]         = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [boardColumns, setBoardColumns] = useState([]);
  const [allCards, setAllCards]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [managers, setManagers]         = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [activeId, setActiveId]         = useState(null);
  const [archivingAll, setArchivingAll] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const [board, result, mgrs] = await Promise.all([
        getBoard(BOARD_ID),
        getCards({ board_id: BOARD_ID, limit: 500 }),
        getManagers(),
      ]);
      setBoardColumns(board?.columns || []);
      setManagers(mgrs || []);
      setAllCards(result?.cards || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  const completeColId = useMemo(
    () => boardColumns.find(c => c.key === COMPLETE_KEY)?.id ?? null,
    [boardColumns]
  );

  const handleArchive = useCallback(async (cardId) => {
    try { await archiveCard(cardId); setAllCards(prev => prev.filter(c => c.id !== cardId)); } catch (e) { console.error(e); }
  }, []);

  const handleArchiveAllCompleted = useCallback(async () => {
    if (!completeColId) return;
    const completedCards = allCards.filter(c => c.column_id === completeColId);
    if (completedCards.length === 0) return;
    setArchivingAll(true);
    try {
      await Promise.all(completedCards.map(c => archiveCard(c.id)));
      setAllCards(prev => prev.filter(c => c.column_id !== completeColId));
    } catch (e) { console.error(e); }
    setArchivingAll(false);
  }, [allCards, completeColId]);

  const filteredCards = allCards.filter(card => {
    if (filterAssignee && String(card.assigned_to) !== String(filterAssignee)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        (card.chemist_name  || '').toLowerCase().includes(q) ||
        (card.pharmacy_name || '').toLowerCase().includes(q) ||
        (card.description   || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const columns = (() => {
    const grouped = {};
    boardColumns.forEach(c => { grouped[String(c.id)] = []; });
    filteredCards.forEach(card => {
      const key = String(card.column_id);
      if (grouped[key]) grouped[key].push(card);
    });
    return grouped;
  })();

  const COLUMN_IDS = new Set(boardColumns.map(c => String(c.id)));

  const kanbanCollision = (args) => {
    const colHits = rectIntersection({ ...args, droppableContainers: args.droppableContainers.filter(c => COLUMN_IDS.has(String(c.id))) });
    if (colHits.length > 0) return colHits;
    return closestCenter(args);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over) return;
    const activeStr = String(active.id);
    const overStr   = String(over.id);
    let sourceCol = null; let card = null;
    for (const [col, cards] of Object.entries(columns)) {
      const found = cards.find(c => String(c.id) === activeStr);
      if (found) { sourceCol = col; card = found; break; }
    }
    if (!sourceCol || !card) return;
    let targetCol = COLUMN_IDS.has(overStr) ? overStr : null;
    if (!targetCol) {
      for (const [col, cards] of Object.entries(columns)) {
        if (cards.find(c => String(c.id) === overStr)) { targetCol = col; break; }
      }
    }
    if (!targetCol || targetCol === sourceCol) return;
    setAllCards(prev => prev.map(c => String(c.id) === activeStr ? { ...c, column_id: Number(targetCol) } : c));
    try { await moveCard(card.id, Number(targetCol)); } catch { loadBoard(); }
  };

  const draggedCard = activeId ? Object.values(columns).flat().find(c => String(c.id) === String(activeId)) : null;
  const firstColumnId = boardColumns[0]?.id || 21;

  if (loading) return <div className="flex justify-center items-center h-full"><CircularProgress style={{ color: '#FFAF3C' }} /></div>;

  return (
    <div className="flex flex-col h-full gap-3 p-1">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 18 }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search pharmacy…"
            className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white w-48" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition">
              <CloseIcon style={{ fontSize: 16 }} />
            </button>
          )}
        </div>
        <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}
          className="py-2 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white">
          <option value="">All assignees</option>
          {managers.map(m => <option key={m.Id} value={m.Id}>{m.Name}</option>)}
        </select>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#FFAF3C] text-[#002169] text-sm font-bold rounded-lg hover:bg-amber-400 transition shadow-sm">
          <AddIcon style={{ fontSize: 18 }} /> New Job
        </button>
        <button onClick={() => setShowArchived(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-gray-400 hover:text-gray-800 transition">
          <ArchiveOutlinedIcon style={{ fontSize: 16 }} /> Archived
        </button>
        {completeColId && allCards.some(c => c.column_id === completeColId) && (
          <button onClick={handleArchiveAllCompleted} disabled={archivingAll}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-400 disabled:opacity-50 transition">
            <ArchiveOutlinedIcon style={{ fontSize: 16 }} />
            {archivingAll ? 'Archiving…' : 'Archive all Completed'}
          </button>
        )}
      </div>

      {/* Board */}
      <DndContext sensors={sensors} collisionDetection={kanbanCollision}
        onDragStart={({ active }) => setActiveId(active.id)}
        onDragEnd={handleDragEnd}>
        <div className="flex gap-4 flex-1 min-h-0">
          {boardColumns.map(col => {
            const colors = COL_COLORS[col.key] || COL_COLORS.new;
            const colCards = columns[String(col.id)] || [];
            return (
              <div key={col.id} className="flex flex-col flex-1 min-w-0 min-h-0 rounded-2xl overflow-hidden border border-gray-200"
                style={{ backgroundColor: colors.bg }}>
                {/* Column header */}
                <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
                  style={{ backgroundColor: colors.header, borderBottom: `2px solid ${colors.accent}` }}>
                  <span className="text-sm font-bold" style={{ color: colors.text }}>{col.label}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: colors.accent }}>
                    {colCards.length}
                  </span>
                </div>
                {/* Card list */}
                <KanbanColumn id={String(col.id)} label={col.label} cards={colCards} accentColor={colors.accent} fullWidth>
                  {colCards.map(card => (
                    <JobCardTile
                      key={card.id}
                      card={card}
                      colKey={col.key}
                      onClick={() => setSelectedCardId(card.id)}
                      onArchive={completeColId && col.id === completeColId ? handleArchive : undefined}
                    />
                  ))}
                </KanbanColumn>
              </div>
            );
          })}
        </div>

        <DragOverlay>
          {draggedCard && (
            <div className="bg-white rounded-xl shadow-xl border-2 border-amber-300 p-4 w-72 rotate-1">
              <p className="text-sm font-bold text-amber-700 truncate">{draggedCard.chemist_name}</p>
              <p className="text-sm text-gray-700 line-clamp-2 mt-1">{draggedCard.description}</p>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {showNew && <NewJobModal firstColumnId={firstColumnId} onClose={() => setShowNew(false)} onCreated={loadBoard} />}
      {selectedCardId && (
        <JobCardDetailModal
          cardId={selectedCardId}
          boardColumns={boardColumns}
          completeColId={completeColId}
          onClose={() => setSelectedCardId(null)}
          onUpdate={loadBoard}
          onArchive={handleArchive}
        />
      )}
      {showArchived && <ArchivedPanel onClose={() => setShowArchived(false)} onUnarchived={loadBoard} />}
    </div>
  );
};

export default JobBoard;
